import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import { ActivityFeedPartsFragment, ActivityType, EntityType } from '../../__generated__/graphql'
import i18n from '../../i18n'
import { displayAsCurrency } from '../../utils/displayAsCurrency'
import { ActivityFeedResolvedEntity, getActivityFeedEntityLabel, getActivityFeedEntityUrl } from './activityFeedEntity'

type ActivityFeedValueSide = 'old' | 'new'

export type ActivityFeedValueDisplay = {
  label: string
  url?: string
}

type ActivityFeedValueContext = Pick<
  ActivityFeedPartsFragment,
  'company' | 'property' | 'oldValue' | 'newValue' | 'oldValueEntity' | 'newValueEntity'
>

const CURRENCY_PROPERTIES = new Set([
  'costValue',
  'dailyBudget',
  'budgetCapping',
  'cpmFloor',
  'budget',
  'cost',
  'cpc',
  'cpm',
  'defaultCpc',
])

const DATE_PROPERTIES = new Set(['startDate', 'endDate'])

const GEO_TARGETING_TRANSLATION_KEYS: Record<string, string> = {
  geoTargeting: 'activity.feed.geoTargetingChanged',
  rawGeoTargeting: 'activity.feed.geoTargetingChanged',
}

const ACTIVITY_FEED_DATE_ENTITY_TYPES = new Set<EntityType>([EntityType.AdGroup, EntityType.AdCopy])

const ACTIVITY_FEED_DATE_LOCALES: Record<string, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  it: 'it-IT',
}

const snakeToCamelCase = (property: string): string =>
  property.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase())

const normalizeActivityFeedProperty = (property: string): string => snakeToCamelCase(property)

const DIRECTORY_PRESET_LIST_ITEM_PROPERTY_ALIASES: Record<string, string> = {
  directoryPositionIds: 'positionIds',
  directoryWebsiteIds: 'websiteIds',
}

const EXCLUSION_PRESET_LIST_ITEM_PROPERTY_ALIASES: Record<string, string> = {
  excludedCompanyIds: 'company',
  excludedCampaignIds: 'campaign',
  excludedIABCategories: 'iabCategory',
  excludedIabCategories: 'iabCategory',
}

const PRESET_LIST_ITEM_PROPERTY_ALIASES: Record<string, string> = {
  ...DIRECTORY_PRESET_LIST_ITEM_PROPERTY_ALIASES,
  ...EXCLUSION_PRESET_LIST_ITEM_PROPERTY_ALIASES,
}

export const isDirectoryPresetListProperty = (property: string | null | undefined): boolean => {
  if (property == null) return false

  return normalizeActivityFeedProperty(property) in DIRECTORY_PRESET_LIST_ITEM_PROPERTY_ALIASES
}

export const isExclusionPresetListProperty = (property: string | null | undefined): boolean => {
  if (property == null) return false

  return normalizeActivityFeedProperty(property) in EXCLUSION_PRESET_LIST_ITEM_PROPERTY_ALIASES
}

export const isPresetListProperty = (property: string | null | undefined): boolean => {
  if (property == null) return false

  return normalizeActivityFeedProperty(property) in PRESET_LIST_ITEM_PROPERTY_ALIASES
}

export const isActivityFeedZoneProperty = (property: string | null | undefined): boolean =>
  property != null && normalizeActivityFeedProperty(property) === 'zones'

export const parseActivityFeedZoneAssignment = (
  value: string | null | undefined
): { zone: string; templateId: string; format?: string } | null => {
  if (!value) return null

  const match = value.match(/^(\d+):(\d+)(?::([A-Za-z0-9_]+))?$/)
  if (!match) return null

  return {
    zone: match[1],
    templateId: match[2],
    format: match[3],
  }
}

const ACTIVITY_FEED_STATUS_PROPERTIES = new Set(['is_active', 'status'])

const AD_TEMPLATE_NATIVE_TEMPLATE_PROPERTIES = new Set(['template', 'mobileTemplate'])

const AD_TEMPLATE_NATIVE_TEMPLATE_ENTITY_TYPES = new Set<EntityType>([
  EntityType.AdTemplate,
  EntityType.AdTemplateNative,
])

export const isActivityFeedAdTemplateNativeTemplateProperty = (property: string | null | undefined): boolean =>
  property != null && AD_TEMPLATE_NATIVE_TEMPLATE_PROPERTIES.has(normalizeActivityFeedProperty(property))

export const isActivityFeedAdTemplateNativeTemplateUpdate = (
  activityType: ActivityType,
  entityType: EntityType | undefined,
  property: string | null | undefined
): boolean =>
  activityType === ActivityType.Update &&
  entityType != null &&
  AD_TEMPLATE_NATIVE_TEMPLATE_ENTITY_TYPES.has(entityType) &&
  isActivityFeedAdTemplateNativeTemplateProperty(property)

export const isActivityFeedUserPasswordProperty = (property: string | null | undefined): boolean =>
  property != null && normalizeActivityFeedProperty(property) === 'password'

export const isActivityFeedUserPasswordUpdate = (
  activityType: ActivityType,
  entityType: EntityType | undefined,
  property: string | null | undefined
): boolean =>
  activityType === ActivityType.Update && entityType === EntityType.User && isActivityFeedUserPasswordProperty(property)

const ACTIVITY_FEED_REDIRECT_URL_PROPERTIES = new Set(['redirectUrl', 'forceRedirectUrl'])

export const isActivityFeedRedirectUrlProperty = (property: string | null | undefined): boolean =>
  property != null && ACTIVITY_FEED_REDIRECT_URL_PROPERTIES.has(normalizeActivityFeedProperty(property))

export const isActivityFeedRedirectUrlUpdate = (
  activityType: ActivityType,
  property: string | null | undefined
): boolean => activityType === ActivityType.Update && isActivityFeedRedirectUrlProperty(property)

export const isActivityFeedImageProperty = (property: string | null | undefined): boolean =>
  property != null && normalizeActivityFeedProperty(property) === 'image'

export const isActivityFeedImageUpdate = (activityType: ActivityType, property: string | null | undefined): boolean =>
  activityType === ActivityType.Update && isActivityFeedImageProperty(property)

export const isActivityFeedPropertyValueEmpty = (value: string | null | undefined): boolean =>
  value == null || value === ''

export const isActivityFeedPropertyClearedUpdate = (
  activityType: ActivityType,
  property: string | null | undefined,
  newValue: string | null | undefined
): boolean =>
  activityType === ActivityType.Update &&
  property != null &&
  !ACTIVITY_FEED_STATUS_PROPERTIES.has(property) &&
  !isActivityFeedUserPasswordProperty(property) &&
  !isActivityFeedRedirectUrlProperty(property) &&
  !isActivityFeedImageProperty(property) &&
  !isActivityFeedAdTemplateNativeTemplateProperty(property) &&
  isActivityFeedPropertyValueEmpty(newValue)

export const getActivityFeedAddRemovePropertyLabelInSentence = (
  property: string | null | undefined,
  t: TFunction
): string => {
  if (!property) return ''

  const normalizedProperty = normalizeActivityFeedProperty(property)
  const labelProperty = PRESET_LIST_ITEM_PROPERTY_ALIASES[normalizedProperty] ?? normalizedProperty

  return getActivityFeedPropertyLabelInSentence(labelProperty, t)
}

export const getActivityFeedGeoTargetingTranslationKey = (property: string | null | undefined): string | undefined => {
  if (property == null) return undefined

  return GEO_TARGETING_TRANSLATION_KEYS[normalizeActivityFeedProperty(property)]
}

export const isActivityFeedGeoTargetingProperty = (property: string | null | undefined): boolean =>
  getActivityFeedGeoTargetingTranslationKey(property) != null

export const isActivityFeedCurrencyProperty = (property: string | null | undefined): boolean =>
  property != null && CURRENCY_PROPERTIES.has(normalizeActivityFeedProperty(property))

export const isActivityFeedDateProperty = (property: string | null | undefined, entityType?: EntityType): boolean => {
  if (property == null) return false
  if (!DATE_PROPERTIES.has(normalizeActivityFeedProperty(property))) return false
  if (entityType == null) return true

  return ACTIVITY_FEED_DATE_ENTITY_TYPES.has(entityType)
}

const resolveActivityFeedDateLocale = (language?: string): string => {
  const resolvedLanguage = (language ?? i18n.language ?? 'en').split('-')[0]

  return ACTIVITY_FEED_DATE_LOCALES[resolvedLanguage] ?? ACTIVITY_FEED_DATE_LOCALES.en
}

export const formatActivityFeedDateValue = (
  value: string | null | undefined,
  language?: string
): string | null | undefined => {
  if (!value) return value

  const unquotedValue = value.trim().replace(/^"+|"+$/g, '')
  const dateOnlyMatch = unquotedValue.match(/^(\d{4}-\d{2}-\d{2})/)
  const parsed = dayjs(dateOnlyMatch?.[1] ?? unquotedValue)
  if (!parsed.isValid()) return value

  return new Intl.DateTimeFormat(resolveActivityFeedDateLocale(language), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parsed.toDate())
}

export const getActivityFeedPropertyLabel = (property: string | null | undefined, t: TFunction): string => {
  if (!property) return ''

  const normalizedProperty = normalizeActivityFeedProperty(property)
  const labelProperty = isActivityFeedAdTemplateNativeTemplateProperty(normalizedProperty)
    ? 'template'
    : normalizedProperty

  return t(`activity.feed.properties.${labelProperty}`, { defaultValue: property })
}

export const getActivityFeedPropertyLabelInSentence = (property: string | null | undefined, t: TFunction): string => {
  const label = getActivityFeedPropertyLabel(property, t)

  if (!label) return ''

  return label.charAt(0).toLocaleLowerCase() + label.slice(1)
}

const ACTIVITY_FEED_ENUM_PROPERTY_TRANSLATION_KEYS: Record<string, string> = {
  gender: 'enum.Gender',
  deviceType: 'enum.DeviceType',
  os: 'enum.OsType',
  osType: 'enum.OsType',
  dayWeek: 'enum.Day',
}

export const getActivityFeedEnumTranslationKey = (property: string | null | undefined): string | undefined => {
  if (!property) return undefined

  return ACTIVITY_FEED_ENUM_PROPERTY_TRANSLATION_KEYS[normalizeActivityFeedProperty(property)]
}

export const formatActivityFeedEnumValue = (
  property: string | null | undefined,
  value: string | null | undefined,
  entityType: EntityType | undefined,
  t: TFunction
): string | undefined => {
  if (!property || value == null || value === '') return undefined

  const normalizedProperty = normalizeActivityFeedProperty(property)

  if (normalizedProperty === 'subType' && entityType === EntityType.Company) {
    return t(`enum.AdvertiserType.${value}`, { defaultValue: value })
  }

  const enumTranslationKey = getActivityFeedEnumTranslationKey(property)

  if (enumTranslationKey) {
    return t(`${enumTranslationKey}.${value}`, { defaultValue: value })
  }

  return undefined
}

type ActivityFeedValueFormatOptions = {
  entityType?: EntityType
  language?: string
}

export const formatActivityFeedValue = (
  property: string | null | undefined,
  value: string | null | undefined,
  options?: ActivityFeedValueFormatOptions
): string | null | undefined => {
  if (isActivityFeedDateProperty(property, options?.entityType)) {
    const formattedDate = formatActivityFeedDateValue(value, options?.language)

    return formattedDate ?? value
  }

  if (!isActivityFeedCurrencyProperty(property)) return value

  const formattedValue = displayAsCurrency(value)

  return formattedValue ?? value
}

const getActivityFeedValueEntity = (
  feed: ActivityFeedValueContext,
  side: ActivityFeedValueSide
): ActivityFeedResolvedEntity | null | undefined => (side === 'old' ? feed.oldValueEntity : feed.newValueEntity)

type ActivityFeedValueDisplayOptions = {
  entityType?: EntityType
  language?: string
  t?: TFunction
}

export const getActivityFeedValueDisplay = (
  feed: ActivityFeedValueContext,
  side: ActivityFeedValueSide,
  options?: ActivityFeedValueDisplayOptions
): ActivityFeedValueDisplay => {
  const valueEntity = getActivityFeedValueEntity(feed, side)

  const rawValue = side === 'old' ? feed.oldValue : feed.newValue

  if (valueEntity != null) {
    const labelFromEntity = getActivityFeedEntityLabel(valueEntity)

    if (labelFromEntity) {
      return {
        label: labelFromEntity,
        url: getActivityFeedEntityUrl({ entity: valueEntity, company: feed.company }),
      }
    }
  }

  if (isActivityFeedZoneProperty(feed.property)) {
    const zoneAssignment = parseActivityFeedZoneAssignment(rawValue)

    if (zoneAssignment)
      return {
        label: zoneAssignment.templateId,
      }
  }

  const enumLabel =
    options?.t != null ? formatActivityFeedEnumValue(feed.property, rawValue, options.entityType, options.t) : undefined

  return {
    label:
      enumLabel ??
      formatActivityFeedValue(feed.property, rawValue, {
        entityType: options?.entityType,
        language: options?.language,
      }) ??
      rawValue ??
      '',
  }
}
